import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import {
  Flex,
  Text,
  IconButton,
  ScrollArea,
  Container,
} from "@radix-ui/themes";
import { EscalationRuleForm } from "@/features/automation-configuration/quackchat-channel/EscalationRuleForm";
import { useSimulatorConfiguration } from "@/features/automation-configuration/useSimulatorConfiguration";
import {
  CreateEscalationRuleRequest,
  useEscalationRuleApi,
} from "@/v2/entities/escalation-rule/EscalationRule.api";
import { toast } from "sonner";
import { useCallback, useState } from "react";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import {
  buildCustomFieldsFromFilters,
  LiveAgentRoutingStrategy,
} from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationRuleFormData } from "@/features/automation-configuration/quackchat-channel/EscalationRuleForm";

function CreateEscalationRulePage() {
  const navigate = useNavigate();
  const { simulatorId } = useParams();
  const { simulator } = useSimulatorConfiguration(simulatorId);
  const { createEscalationRule } = useEscalationRuleApi(simulatorId);
  const [isCreating, setIsCreating] = useState(false);

  const allowLiveAgent = simulator?.channel === "autonomous_quackchat";

  const handleSubmit = useCallback(
    async (formData: EscalationRuleFormData) => {
      try {
        setIsCreating(true);
        const customFields = buildCustomFieldsFromFilters(
          formData.customFieldFilters
        );
        const payload: CreateEscalationRuleRequest = {
          name: formData.name,
          topics: formData.topics,
          customFields,
          escalationStrategy: formData.escalationStrategy,
          systemMessage:
            formData.escalationStrategy === EscalationStrategy.LIVE_AGENT
              ? ""
              : formData.systemMessage,
          escalationRuleOperations: formData.escalationRuleOperations,
          simulatorId: simulatorId ?? undefined,
          liveAgentRoutingStrategy:
            formData.escalationStrategy === EscalationStrategy.LIVE_AGENT
              ? formData.liveAgentRoutingStrategy ??
                LiveAgentRoutingStrategy.ONLINE_AGENTS
              : undefined,
        };

        await createEscalationRule(payload);
        toast.success("Escalation rule created successfully");
        navigate(`/automation/${simulatorId}/configurations/escalations`);
      } catch (error) {
        console.error("Failed to create escalation rule:", error);
        toast.error("Failed to create escalation rule");
      } finally {
        setIsCreating(false);
      }
    },
    [createEscalationRule, navigate, simulatorId]
  );

  const handleCancel = () => {
    navigate(`/automation/${simulatorId}/configurations/escalations`);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "var(--backgroundDefault)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Flex
        justify="start"
        align="center"
        py={"4"}
        px={"8"}
        gap={"4"}
        style={{
          borderBottom: "1px solid var(--q-border)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <IconButton variant="ghost" color="gray" onClick={handleCancel}>
          <ArrowLeftIcon />
        </IconButton>

        <Text size="2" color="gray" weight="medium" highContrast>
          New Escalation Rule
        </Text>
      </Flex>

      <ScrollArea style={{ flexGrow: 1 }}>
        <Container size="4" style={{ paddingBlock: 32 }}>
          <EscalationRuleForm
            onSubmit={handleSubmit}
            loading={isCreating}
            allowLiveAgent={allowLiveAgent}
          />
        </Container>
      </ScrollArea>
    </div>
  );
}

export default CreateEscalationRulePage;
